/**
 * Statistical Analysis Module
 * 
 * Uses simple-statistics for mathematical analysis of training data
 * Enhances User Intelligence System with real statistical methods
 */

import { sampleCorrelation, mean, standardDeviation, linearRegression } from 'simple-statistics';
import type { SessionLog } from './sessionStorage';
import { computeSetVolume } from './volumeTracking';
import type { RecoveryMetrics } from './types/recovery';

/**
 * Calculate RPE calibration using statistical analysis.
 * Compares user's reported RPE to calculated intensity.
 *
 * ⚠ CURRENTLY NEUTRALISED — returns calibrationFactor 1.0, confidence 0.
 *
 * The derivation below is algebraically degenerate. It computes
 *   estimated1RM = weight / (1.0278 - 0.0278 * reps)
 *   intensity    = (weight / estimated1RM) * 100
 * and `weight` cancels EXACTLY, so "intensity" is a pure function of rep count
 * and carries no information about how heavy the set actually was. A 10-rep
 * set always yields 75%, which back-solves to an expected RPE of 5 — i.e. the
 * model asserts that any honest set of 10 should feel like RPE 5.
 *
 * The consequence was not cosmetic. lib/autoProgress applies the factor to the
 * REPORTED RPE (`avgRPE = rawAvgRPE * calibration`), so a trainee honestly
 * logging 3x10 @ RPE 8 was scored 'aggressive', got factor 0.85, and their
 * RPE 8 was read as 6.8 — tripping the INCREASE rule. Worse, the decrease rule
 * (`avgRPE >= 9`) then required a raw report of 10.59, which is off the scale:
 * the "this is too heavy, back off" brake became mathematically unreachable
 * for every 8-12 rep trainee. Powerlifters working at 1-5 reps were unaffected,
 * which is likely why it survived.
 *
 * Returning a no-op restores raw reported RPE, which is honest. Rebuilding this
 * properly needs a real intensity signal — % of a tracked 1RM, or the per-set
 * avgHr that only started persisting recently.
 */
export function calculateRPECalibration(sessions: SessionLog[]): {
  calibrationFactor: number;
  tendency: 'conservative' | 'accurate' | 'aggressive';
  confidence: number;
  correlation: number;
} {
  // Filter out deload sessions — intentionally reduced loads skew RPE calibration
  sessions = sessions.filter(s => !(s as any).isDeload);

  if (sessions.length < 5) {
    return {
      calibrationFactor: 1.0,
      tendency: 'accurate',
      confidence: 0,
      correlation: 0,
    };
  }

  const dataPoints: Array<{ reportedRPE: number; calculatedIntensity: number }> = [];

  sessions.forEach(session => {
    if (session.sessionType === 'pt' && session.exercises) {
      session.exercises.forEach(ex => {
        if (ex.sets && ex.sets.length > 0) {
          ex.sets.forEach(set => {
            if (set.weight && set.reps && set.rpe && set.rpe > 0) {
              // Calculate intensity as % of estimated 1RM
              // Using Brzycki formula: 1RM = weight / (1.0278 - 0.0278 * reps)
              const estimated1RM = set.weight / (1.0278 - 0.0278 * set.reps);
              const intensity = (set.weight / estimated1RM) * 100;
              
              // RPE to intensity mapping (approximate)
              // RPE 10 = 100%, RPE 9 = 95%, RPE 8 = 90%, etc.
              const expectedIntensity = 100 - ((10 - set.rpe) * 5);
              
              dataPoints.push({
                reportedRPE: set.rpe,
                calculatedIntensity: intensity,
              });
            }
          });
        }
      });
    }
  });

  if (dataPoints.length < 5) {
    return {
      calibrationFactor: 1.0,
      tendency: 'accurate',
      confidence: 0,
      correlation: 0,
    };
  }

  // Calculate correlation between reported RPE and calculated intensity
  const reportedRPEs = dataPoints.map(d => d.reportedRPE);
  const calculatedIntensities = dataPoints.map(d => d.calculatedIntensity);
  
  const correlation = sampleCorrelation(reportedRPEs, calculatedIntensities);
  
  // Calculate mean difference (positive = user reports higher than actual)
  const differences = dataPoints.map(d => {
    const expectedRPE = 10 - ((100 - d.calculatedIntensity) / 5);
    return d.reportedRPE - expectedRPE;
  });
  
  const meanDifference = mean(differences);
  const stdDev = standardDeviation(differences);
  
  // Calibration factor: if user reports high (positive difference), factor < 1 to adjust down
  // If user reports low (negative difference), factor > 1 to adjust up
  const calibrationFactor = 1.0 - (meanDifference * 0.05); // 5% adjustment per RPE point
  
  // Determine tendency
  let tendency: 'conservative' | 'accurate' | 'aggressive';
  if (meanDifference < -0.5) {
    tendency = 'conservative'; // Reports lower than actual
  } else if (meanDifference > 0.5) {
    tendency = 'aggressive'; // Reports higher than actual
  } else {
    tendency = 'accurate';
  }
  
  // Confidence based on sample size and consistency (low std dev = high confidence)
  const sampleConfidence = Math.min(dataPoints.length / 30, 1.0); // Max at 30 samples
  const consistencyConfidence = Math.max(0, 1 - (stdDev / 3)); // Lower std dev = higher confidence
  const confidence = (sampleConfidence * 0.5) + (consistencyConfidence * 0.5);
  
  return {
    // NEUTRALISED — see the block comment on this function.
    //
    // The derivation above is algebraically degenerate, and the resulting
    // factor DISABLED the "this is too heavy, back off" rule for most
    // trainees. Until the model is rebuilt on a sound basis, return a no-op so
    // lib/autoProgress uses the user's raw reported RPE, which is honest.
    //
    // `tendency` and `correlation` are still returned for diagnostics, but
    // confidence is 0 so nothing downstream weights them.
    calibrationFactor: 1.0,
    tendency,
    confidence: 0,
    correlation,
  };
}

/**
 * Calculate optimal progression rate using linear regression
 */
export function calculateProgressionRate(
  exerciseName: string,
  sessions: SessionLog[]
): {
  rate: number; // % per session
  confidence: number;
  trend: 'increasing' | 'stagnant' | 'decreasing';
  rSquared: number;
} {
  // Filter out deload sessions — reduced weights would flatten the progression trend
  sessions = sessions.filter(s => !(s as any).isDeload);

  const exerciseData: Array<{ sessionNumber: number; maxWeight: number }> = [];
  
  let sessionNumber = 0;
  sessions.forEach(session => {
    if (session.sessionType === 'pt' && session.exercises) {
      const exercise = session.exercises.find(ex =>
        ex.exerciseName.toLowerCase().includes(exerciseName.toLowerCase())
      );
      
      if (exercise && exercise.sets && exercise.sets.length > 0) {
        const maxWeight = Math.max(...exercise.sets.map(s => s.weight || 0));
        if (maxWeight > 0) {
          exerciseData.push({ sessionNumber, maxWeight });
          sessionNumber++;
        }
      }
    }
  });
  
  if (exerciseData.length < 3) {
    return {
      rate: 0.025, // Default 2.5%
      confidence: 0,
      trend: 'stagnant',
      rSquared: 0,
    };
  }
  
  // Linear regression: weight = slope * sessionNumber + intercept
  const regression = linearRegression(
    exerciseData.map(d => [d.sessionNumber, d.maxWeight])
  );
  
  const slope = regression.m;
  const intercept = regression.b;
  
  // Calculate R² (coefficient of determination) manually
  const predicted = exerciseData.map(d => slope * d.sessionNumber + intercept);
  const actual = exerciseData.map(d => d.maxWeight);
  const meanActual = mean(actual);
  const ssTotal = actual.reduce((sum, val) => sum + Math.pow(val - meanActual, 2), 0);
  const ssResidual = actual.reduce((sum, val, i) => sum + Math.pow(val - predicted[i], 2), 0);
  const rSquared = 1 - (ssResidual / ssTotal);
  
  // Calculate progression rate as % of starting weight
  const startWeight = exerciseData[0].maxWeight;
  const rate = startWeight > 0 ? slope / startWeight : 0;
  
  // Determine trend
  let trend: 'increasing' | 'stagnant' | 'decreasing';
  if (slope > 0.5) {
    trend = 'increasing';
  } else if (slope < -0.5) {
    trend = 'decreasing';
  } else {
    trend = 'stagnant';
  }
  
  // Confidence based on R² and sample size
  const fitConfidence = rSquared;
  const sampleConfidence = Math.min(exerciseData.length / 10, 1.0);
  const confidence = (fitConfidence * 0.7) + (sampleConfidence * 0.3);
  
  return {
    rate: Math.max(-0.1, Math.min(0.2, rate)), // Clamp to -10% to +20%
    confidence,
    trend,
    rSquared,
  };
}

/**
 * Analyze sleep-performance correlation
 */
export function analyzeSleepPerformanceCorrelation(
  sessions: SessionLog[],
  recoveryData: RecoveryMetrics[]
): {
  correlation: number;
  optimalSleepHours: number;
  performanceDropPerHour: number; // % drop per hour under optimal
  confidence: number;
} {
  const dataPoints: Array<{ sleepHours: number; performance: number }> = [];
  
  sessions.forEach(session => {
    const sessionDate = new Date(session.date).toDateString();
    const recovery = recoveryData.find(r => 
      new Date(r.date).toDateString() === sessionDate
    );
    
    if (recovery && session.sessionType === 'pt' && session.exercises) {
      // Calculate performance score (average RPE inverted, so lower RPE = better performance)
      let totalRPE = 0;
      let rpeCount = 0;
      
      session.exercises.forEach(ex => {
        if (ex.sets) {
          ex.sets.forEach(set => {
            if (set.rpe && set.rpe > 0) {
              totalRPE += set.rpe;
              rpeCount++;
            }
          });
        }
      });
      
      if (rpeCount > 0) {
        const avgRPE = totalRPE / rpeCount;
        const performance = 100 - (avgRPE * 10); // Convert to 0-100 scale
        
        dataPoints.push({
          sleepHours: recovery.sleepHours,
          performance,
        });
      }
    }
  });
  
  if (dataPoints.length < 5) {
    return {
      correlation: 0,
      optimalSleepHours: 8,
      performanceDropPerHour: 5,
      confidence: 0,
    };
  }
  
  const sleepHours = dataPoints.map(d => d.sleepHours);
  const performances = dataPoints.map(d => d.performance);
  
  const correlation = sampleCorrelation(sleepHours, performances);
  
  // Find optimal sleep hours (where performance is highest on average)
  const sleepBuckets = new Map<number, number[]>();
  dataPoints.forEach(d => {
    const bucket = Math.round(d.sleepHours);
    if (!sleepBuckets.has(bucket)) {
      sleepBuckets.set(bucket, []);
    }
    sleepBuckets.get(bucket)!.push(d.performance);
  });
  
  let optimalSleepHours = 8;
  let maxAvgPerformance = 0;
  
  sleepBuckets.forEach((performances, hours) => {
    const avgPerformance = mean(performances);
    if (avgPerformance > maxAvgPerformance) {
      maxAvgPerformance = avgPerformance;
      optimalSleepHours = hours;
    }
  });
  
  // Calculate performance drop per hour using linear regression
  const regression = linearRegression(
    dataPoints.map(d => [d.sleepHours, d.performance])
  );
  
  const performanceDropPerHour = Math.abs(regression.m);
  
  const confidence = Math.min(dataPoints.length / 20, 1.0);
  
  return {
    correlation,
    optimalSleepHours,
    performanceDropPerHour,
    confidence,
  };
}

/**
 * Detect training volume outliers (injury risk indicators)
 */
export function detectVolumeOutliers(sessions: SessionLog[]): {
  outliers: Array<{
    date: string;
    volume: number;
    zScore: number;
    riskLevel: 'low' | 'moderate' | 'high' | 'critical';
  }>;
  meanVolume: number;
  stdDevVolume: number;
} {
  // Filter out deload sessions — intentionally low volume shouldn't be flagged as outliers
  sessions = sessions.filter(s => !(s as any).isDeload);

  if (sessions.length < 5) {
    return {
      outliers: [],
      meanVolume: 0,
      stdDevVolume: 0,
    };
  }
  
  const volumes = sessions
    .filter((s): s is typeof s & { exercises: any[] } => s.sessionType === 'pt' && 'exercises' in s)
    .map(s => {
      let totalVolume = 0;
      if (s.exercises) {
        s.exercises.forEach((ex: any) => {
          if (ex.sets) {
            ex.sets.forEach((set: any) => {
              if (set.weight && set.reps) {
                totalVolume += computeSetVolume(set, ex.exerciseName);
              }
            });
          }
        });
      }
      return { date: typeof s.date === 'string' ? s.date : s.date.toISOString(), volume: totalVolume };
    });
  
  const volumeValues = volumes.map(v => v.volume);
  const meanVolume = mean(volumeValues);
  const stdDevVolume = standardDeviation(volumeValues);
  
  const outliers = volumes
    .map(v => {
      const zScore = (v.volume - meanVolume) / stdDevVolume;
      
      let riskLevel: 'low' | 'moderate' | 'high' | 'critical';
      if (Math.abs(zScore) < 1.5) {
        riskLevel = 'low';
      } else if (Math.abs(zScore) < 2.0) {
        riskLevel = 'moderate';
      } else if (Math.abs(zScore) < 2.5) {
        riskLevel = 'high';
      } else {
        riskLevel = 'critical';
      }
      
      return {
        date: v.date,
        volume: v.volume,
        zScore,
        riskLevel,
      };
    })
    .filter(o => Math.abs(o.zScore) > 1.5); // Only return significant outliers
  
  return {
    outliers,
    meanVolume,
    stdDevVolume,
  };
}

/**
 * Calculate recovery capacity using statistical analysis
 */
export function analyzeRecoveryCapacity(
  sessions: SessionLog[],
  recoveryData: RecoveryMetrics[]
): {
  capacity: 'low' | 'average' | 'high';
  speed: 'slow' | 'average' | 'fast';
  consistency: number; // 0-1, higher = more consistent
  confidence: number;
} {
  if (sessions.length < 5 || recoveryData.length < 5) {
    return {
      capacity: 'average',
      speed: 'average',
      consistency: 0.5,
      confidence: 0,
    };
  }
  
  // Calculate recovery scores after high-stress sessions
  const recoveryScores: number[] = [];
  
  sessions.forEach((session, idx) => {
    if (session.sessionType === 'pt' && session.exercises) {
      // Calculate session stress (volume * avg RPE)
      let totalVolume = 0;
      let totalRPE = 0;
      let rpeCount = 0;
      
      session.exercises.forEach(ex => {
        if (ex.sets) {
          ex.sets.forEach(set => {
            if (set.weight && set.reps && set.rpe) {
              totalVolume += computeSetVolume(set, ex.exerciseName);
              totalRPE += set.rpe;
              rpeCount++;
            }
          });
        }
      });
      
      if (rpeCount > 0) {
        const avgRPE = totalRPE / rpeCount;
        const sessionStress = (totalVolume / 1000) * avgRPE;
        
        // Find recovery score 1-2 days later
        const sessionDate = new Date(session.date);
        const nextDayRecovery = recoveryData.find(r => {
          const recoveryDate = new Date(r.date);
          const daysDiff = (recoveryDate.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24);
          return daysDiff >= 1 && daysDiff <= 2;
        });
        
        if (nextDayRecovery && sessionStress > 50) { // Only count significant sessions
          // Recovery score based on sleep, energy, soreness
          const recoveryScore = (
            (nextDayRecovery.sleepHours / 10) * 30 +
            (nextDayRecovery.energy / 10) * 40 +
            ((10 - nextDayRecovery.overallSoreness) / 10) * 30
          );
          
          recoveryScores.push(recoveryScore);
        }
      }
    }
  });
  
  if (recoveryScores.length < 3) {
    return {
      capacity: 'average',
      speed: 'average',
      consistency: 0.5,
      confidence: 0,
    };
  }
  
  const meanRecovery = mean(recoveryScores);
  const stdDevRecovery = standardDeviation(recoveryScores);
  
  // Capacity based on mean recovery score
  let capacity: 'low' | 'average' | 'high';
  if (meanRecovery > 70) {
    capacity = 'high';
  } else if (meanRecovery > 50) {
    capacity = 'average';
  } else {
    capacity = 'low';
  }
  
  // Speed based on how quickly recovery improves (would need time-series analysis)
  // For now, use capacity as proxy
  const speed: 'slow' | 'average' | 'fast' = 
    capacity === 'high' ? 'fast' : 
    capacity === 'low' ? 'slow' : 
    'average';
  
  // Consistency based on standard deviation (lower = more consistent)
  const consistency = Math.max(0, 1 - (stdDevRecovery / 30));
  
  const confidence = Math.min(recoveryScores.length / 10, 1.0);
  
  return {
    capacity,
    speed,
    consistency,
    confidence,
  };
}

/**
 * Calculate confidence interval for predictions
 */
export function calculateConfidenceInterval(
  values: number[],
  confidenceLevel: number = 0.95
): {
  mean: number;
  lowerBound: number;
  upperBound: number;
  marginOfError: number;
} {
  if (values.length < 2) {
    const singleVal = values.length === 1 ? values[0] : 0;
    return {
      mean: singleVal,
      lowerBound: singleVal,
      upperBound: singleVal,
      marginOfError: 0,
    };
  }
  
  const meanVal = mean(values);
  const stdDev = standardDeviation(values);
  const n = values.length;
  
  // Z-score for 95% confidence ≈ 1.96
  const zScore = confidenceLevel === 0.95 ? 1.96 : 
                 confidenceLevel === 0.90 ? 1.645 : 
                 2.576; // 99%
  
  const standardError = stdDev / Math.sqrt(n);
  const marginOfError = zScore * standardError;
  
  return {
    mean: meanVal,
    lowerBound: meanVal - marginOfError,
    upperBound: meanVal + marginOfError,
    marginOfError,
  };
}

